import { Delete, Upload } from "@mui/icons-material";
import { CircularProgress, Container, FormControl, IconButton, InputLabel, MenuItem, Select } from "@mui/material";
import { Brand, deleteImage, getBrands, Image, ImageDataUpload, putImage, SessionUser } from "api";
import { ImagesGallery } from "components/ImageGallery/ImageGallery";
import { TableHeader } from "components/Table/Header";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface ImagesPageProps {
  user: SessionUser
}

function getBase64(file: any): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

export function ImagesPage({ user }: ImagesPageProps) {
  const [isUploading, setIsUploading] = useState(false);
  const intl = useIntl();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeBrand, setActiveBrand] = useState<Brand>();
  const [imageGalleryApi, setImageGalleryApi] = useState<{ data: Partial<Image>[], setData: Function }>();

  const onImageUpload = async ({ event }: any) => {
    const file = event.target.files?.[0];

    if (activeBrand && file) {
      setIsUploading(true);
      try {
        const imageData = await getBase64(file);
        const image: ImageDataUpload = {
          id: `${Date.now()}`,
          brandId: activeBrand.id,
          name: file.name.split('.')[0],
          data: imageData
        };
        const resultResponse = await putImage(image);
        imageGalleryApi?.data.push(resultResponse);
      } catch (err) { }
      setIsUploading(false)
    }
  };

  const onImageDelete = async ({ id, brandId }: Image) => {
    await deleteImage({ id, brandId });
    imageGalleryApi?.setData([...imageGalleryApi?.data.filter(({ id: imageId }) => imageId !== id)]);
  };

  useEffect(() => {
    getBrands().then((brands) => {
      setBrands(brands);
      setActiveBrand(brands[0]);
    })
  }, [setBrands]);

  return (
    <Container style={{ marginTop: '80px', minHeight: '300px' }}>
      <TableHeader title={`${intl.formatMessage({id: 'app.heading.images'})} (${activeBrand?.id})`}>
        <>
          {activeBrand && brands?.length > 1 && (
            <FormControl>
              <InputLabel id="brands-select-label">Brand</InputLabel>
              <Select
                sx={{ marginRight: '7px', minWidth: '100px' }}
                size='small'
                labelId="brands-select-label"
                id="brands-select"
                value={activeBrand?.id}
                label="Brand"
                onChange={(event) => {
                  setActiveBrand(brands.find(({ id }) => id === event.target.value));
                }}
              >
                {brands.map(({ id }) => (
                  <MenuItem value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={(event) => onImageUpload({ event })}
            />
            <label htmlFor="image-upload">
              <IconButton component="span">
                {isUploading ? <CircularProgress size={20} /> : <Upload />}
              </IconButton>
            </label>
          </>
        </>
      </TableHeader>

      {activeBrand && (
        <ImagesGallery
          activeBrand={activeBrand}
          actionButton={user.roles.includes('global-admin') ? (item: Image) => (
            <IconButton onClick={() => onImageDelete(item)}>
              <Delete />
            </IconButton>
          ) : undefined}
          setApi={setImageGalleryApi}
        />
      )}
    </Container>
  );
}
